// Login state is not an Inertia prop — that would poison the SSR cache key. Rails puts it in a
// <meta name="sf-auth-state"> instead, and the store starts `ready: false` so hydration matches.

import { router } from '@inertiajs/svelte'
import type { AdVisibility } from 'frontend/shared/javascripts/video_player/types'
import { resetNavigation } from 'frontend/inertia/stores/navigation.svelte'
import {
  syncSessionData,
  type SyncElement,
  type SyncSessionData,
} from 'frontend/shared/javascripts/util/session_sync'

export type ManagedCompany = { kind: 'single'; companyId: number } | { kind: 'several' }

// mirrors InertiaHelper#inertia_user_state
export interface AuthUser {
  id: number
  academyUser: boolean
  academyRole: 'admin' | 'trainer' | null
  assignedLearningPlans: boolean
  customerSideAdministrator: boolean
  managedCompany: ManagedCompany | null
  adminDashboards: boolean
  userHomeAvailable: boolean
  lastCompletedStep: string
}

interface AuthStore {
  ready: boolean
  user: AuthUser | null
  studybuddyMenuVisible: boolean
}

export const auth = $state<AuthStore>({ ready: false, user: null, studybuddyMenuVisible: false })

// `'unknown'` until the store resolves: ad frames already render then, since hydration must match what an
// anonymous viewer gets, but nothing requests an ad before `'shown'`.
export type { AdVisibility }

export function adVisibility(): AdVisibility {
  if (!auth.ready) {
    return 'unknown'
  }
  return auth.user?.academyUser ? 'hidden' : 'shown'
}

// mirrors InertiaHelper#inertia_auth_state
export interface AuthState {
  user: AuthUser | null
  studybuddyMenuVisible: boolean
  sessionSync: SyncSessionData | null
}

// Call from the layout's onMount, after hydration.
export function hydrateAuthFromDom(): void {
  if (import.meta.env.SSR) {
    throw new Error('this must never be called on the SSR server')
  }

  const { user, studybuddyMenuVisible, sessionSync } = readAuthStateFromDom()

  setupAuth(user, { studybuddyMenuVisible })
  void bootSessionSync(sessionSync)
}

// An Inertia visit after a sign in doesn't render the meta tag again (Inertia::AuthStateAfterSignIn).
export function watchAuthStateFlash(): () => void {
  return router.on('flash', (event) => {
    const state = event.detail.flash.auth_state
    if (state) applyAuthState(state)
  })
}

export function applyAuthState({ user, studybuddyMenuVisible, sessionSync }: AuthState): void {
  // Already read from the meta tag.
  if (auth.ready && auth.user?.id === user?.id) return

  setupAuth(user, { studybuddyMenuVisible })
  // Read by the header's styles (Header.svelte).
  document.body.toggleAttribute('data-signed-in', !!user)
  resetNavigation()
  void bootSessionSync(sessionSync)
}

const ANONYMOUS: AuthState = { user: null, studybuddyMenuVisible: false, sessionSync: null }

export function readAuthStateFromDom(): AuthState {
  const raw = document.querySelector('meta[name="sf-auth-state"]')?.getAttribute('content')
  if (!raw) {
    return ANONYMOUS
  }

  try {
    return { ...ANONYMOUS, ...(JSON.parse(raw) as Partial<AuthState>) }
  } catch {
    return ANONYMOUS
  }
}

export function setupAuth(
  user: AuthUser | null,
  { studybuddyMenuVisible = false }: { studybuddyMenuVisible?: boolean } = {},
): void {
  auth.user = user
  auth.studybuddyMenuVisible = studybuddyMenuVisible
  auth.ready = true
}

// Used in specs
export function resetAuth(): void {
  auth.ready = false
  auth.user = null
  auth.studybuddyMenuVisible = false
}

export function bootSessionSync(data: SyncSessionData | null): Promise<void> {
  if (!data) return Promise.resolve()

  const waitForSync = syncSessionData(data)
  // System specs wait on this
  const element = document.querySelector<SyncElement>('meta[name="sf-auth-state"]')
  if (element) element.waitForSync = waitForSync

  return waitForSync
}
